import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { Button } from './primitives'
import { Modal } from './overlay'

interface ConfirmOptions {
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
}

type Resolver = (ok: boolean) => void

const Ctx = createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null)

/** Replaces `window.confirm`, which cannot show campaign context or be styled. */
export function useConfirm() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: Resolver } | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => setState({ opts, resolve }))
  }, [])

  const close = (ok: boolean) => {
    state?.resolve(ok)
    setState(null)
  }

  const value = useMemo(() => confirm, [confirm])

  return (
    <Ctx.Provider value={value}>
      {children}
      <Modal
        open={Boolean(state)}
        onClose={() => close(false)}
        title={state?.opts.title ?? ''}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              {state?.opts.cancelLabel ?? 'キャンセル'}
            </Button>
            <Button variant={state?.opts.danger ? 'danger' : 'primary'} onClick={() => close(true)}>
              {state?.opts.confirmLabel ?? '実行'}
            </Button>
          </>
        }
      >
        <div className="text-sm leading-relaxed text-ink-2">{state?.opts.body}</div>
      </Modal>
    </Ctx.Provider>
  )
}
